/** @file Share Dialog — create/copy/qr/revoke share link. */
import QRCode from "qrcode";
import { h, clear } from "./dom.js";
import { openModal } from "./modal.js";
import { openConfirmDialog } from "./confirm-dialog.js";
import { showToast } from "./toast.js";
import {
  createShareForPlay,
  revokeShareForPlay,
  listActiveSharesForPlay,
  buildShareUrl,
} from "../core/share-actions.js";

/** @typedef {import("../types/schemas.js").ShareDoc} ShareDoc */

/**
 * Mount the Share Dialog for a given play. Resolves when the user closes it.
 * @param {{ ownerId: string, playId: string, playName: string }} opts
 * @returns {Promise<void>}
 */
export function mountShareDialog(opts) {
  return openModal((close) => {
    const container = h("div", {
      class: "flex flex-col gap-4 max-w-md",
      "data-testid": "share-dialog",
    });

    const title = h("h2", { class: "text-xl font-bold" }, [`Spielzug teilen`]);
    const subtitle = h("p", { class: "text-sm text-neutral-400" }, [opts.playName]);
    const body = h("div", { class: "flex flex-col gap-4 min-h-[8rem]" });

    const closeBtn = h(
      "button",
      {
        type: "button",
        class: "hb-btn min-h-11",
        "data-testid": "share-dialog-close",
      },
      ["Schließen"],
    );
    closeBtn.addEventListener("click", () => close(null));

    container.appendChild(title);
    container.appendChild(subtitle);
    container.appendChild(body);
    container.appendChild(h("div", { class: "flex justify-end" }, [closeBtn]));

    // Initial state: loading existing shares.
    renderLoading(body);
    void loadState(body, opts, close);

    return container;
  });
}

/**
 * @param {HTMLElement} host
 */
function renderLoading(host) {
  clear(host);
  host.appendChild(
    h("div", { class: "text-sm text-neutral-400", "data-testid": "share-loading" }, [
      "Lade …",
    ]),
  );
}

/**
 * @param {HTMLElement} host
 * @param {{ ownerId: string, playId: string, playName: string }} opts
 * @param {(result?: any) => void} close
 */
async function loadState(host, opts, close) {
  try {
    const shares = await listActiveSharesForPlay(opts.ownerId, opts.playId);
    if (shares.length > 0 && shares[0]) {
      renderActiveShare(host, opts, shares[0], close);
    } else {
      renderEmpty(host, opts, close);
    }
  } catch (err) {
    console.warn("[share-dialog] load failed", err);
    renderError(host);
  }
}

/**
 * @param {HTMLElement} host
 */
function renderError(host) {
  clear(host);
  host.appendChild(
    h(
      "div",
      {
        class: "text-sm text-red-400",
        "data-testid": "share-error",
      },
      ["Teilen konnte nicht geladen werden. Bitte versuche es erneut."],
    ),
  );
}

/**
 * @param {HTMLElement} host
 * @param {{ ownerId: string, playId: string, playName: string }} opts
 * @param {(result?: any) => void} close
 */
function renderEmpty(host, opts, close) {
  clear(host);
  const info = h("p", { class: "text-sm text-neutral-300" }, [
    "Erstelle einen Link, um diesen Spielzug mit deinem Team zu teilen.",
  ]);
  const createBtn = /** @type {HTMLButtonElement} */ (
    h(
      "button",
      {
        type: "button",
        class: "hb-btn hb-btn--primary min-h-11 min-w-11",
        "data-testid": "share-create",
      },
      ["Link erstellen"],
    )
  );
  createBtn.addEventListener("click", async () => {
    createBtn.disabled = true;
    createBtn.textContent = "Erstelle …";
    try {
      const { shareId } = await createShareForPlay(opts.ownerId, opts.playId);
      const share = {
        shareId,
        ownerId: opts.ownerId,
        playId: opts.playId,
        createdAt: new Date(),
        revoked: false,
      };
      renderActiveShare(host, opts, share, close);
      showToast("Link erstellt");
    } catch (err) {
      console.warn("[share-dialog] create failed", err);
      createBtn.disabled = false;
      createBtn.textContent = "Link erstellen";
      showToast("Link konnte nicht erstellt werden");
    }
  });
  host.appendChild(info);
  host.appendChild(createBtn);
}

/**
 * Safely inject an SVG string produced by the trusted `qrcode` library. We
 * parse into a document fragment and adopt the root node — no user-supplied
 * content flows through this helper.
 * @param {HTMLElement} host
 * @param {string} svgString
 */
function mountSvg(host, svgString) {
  clear(host);
  const parsed = new DOMParser().parseFromString(svgString, "image/svg+xml");
  const svg = parsed.documentElement;
  if (svg && svg.nodeName.toLowerCase() === "svg") {
    host.appendChild(host.ownerDocument.importNode(svg, true));
  }
}

/**
 * @param {HTMLElement} host
 * @param {{ ownerId: string, playId: string, playName: string }} opts
 * @param {ShareDoc} share
 * @param {(result?: any) => void} close
 */
function renderActiveShare(host, opts, share, close) {
  clear(host);
  const url = buildShareUrl(share.shareId);

  // Link row
  const input = h("input", {
    type: "text",
    value: url,
    readonly: true,
    class:
      "flex-1 min-h-11 bg-neutral-800 text-neutral-100 rounded px-3 text-sm border border-neutral-700",
    "data-testid": "share-url",
    "aria-label": "Share-Link",
  });
  const copyBtn = /** @type {HTMLButtonElement} */ (
    h(
      "button",
      {
        type: "button",
        class: "hb-btn hb-btn--primary min-h-11 min-w-11",
        "data-testid": "share-copy",
      },
      ["Link kopieren"],
    )
  );
  const initialLabel = "Link kopieren";
  /** @type {any} */
  let copyResetTimer = null;
  copyBtn.addEventListener("click", async () => {
    try {
      await copyToClipboard(url);
      copyBtn.textContent = "Kopiert!";
      if (copyResetTimer) {
        clearTimeout(copyResetTimer);
      }
      copyResetTimer = setTimeout(() => {
        copyBtn.textContent = initialLabel;
      }, 3000);
    } catch (err) {
      console.warn("[share-dialog] copy failed", err);
      showToast("Link konnte nicht kopiert werden");
    }
  });

  const linkRow = h("div", { class: "flex gap-2 items-stretch" }, [
    /** @type {HTMLElement} */ (input),
    copyBtn,
  ]);

  // QR-Code (thick white border for dark-hall scanability).
  const qrHost = h("div", {
    class: "p-2 bg-white rounded inline-flex",
    "data-testid": "share-qr",
    "aria-label": "QR-Code des Share-Links",
  });
  QRCode.toString(url, {
    type: "svg",
    margin: 2,
    width: 200,
    color: { dark: "#000000", light: "#ffffff" },
  })
    .then((/** @type {string} */ svg) => {
      mountSvg(qrHost, svg);
    })
    .catch((/** @type {unknown} */ err) => {
      console.warn("[share-dialog] QR render failed", err);
      qrHost.textContent = "QR nicht verfügbar";
    });

  const qrRow = h("div", { class: "flex justify-center" }, [qrHost]);

  // Revoke button
  const revokeBtn = /** @type {HTMLButtonElement} */ (
    h(
      "button",
      {
        type: "button",
        class: "hb-btn hb-btn--danger min-h-11 min-w-11",
        "data-testid": "share-revoke",
      },
      ["Link widerrufen"],
    )
  );
  revokeBtn.addEventListener("click", async () => {
    const ok = await openConfirmDialog({
      title: "Link widerrufen?",
      body: "Nach dem Widerrufen funktioniert der Link nicht mehr. Trotzdem widerrufen?",
      destructive: true,
      confirmLabel: "Widerrufen",
    });
    if (!ok) {
      return;
    }
    revokeBtn.disabled = true;
    revokeBtn.textContent = "Widerrufe …";
    try {
      await revokeShareForPlay(opts.ownerId, opts.playId, share.shareId);
      showToast("Link widerrufen");
      close(null);
    } catch (err) {
      console.warn("[share-dialog] revoke failed", err);
      revokeBtn.disabled = false;
      revokeBtn.textContent = "Link widerrufen";
      showToast("Widerrufen fehlgeschlagen");
    }
  });

  const hint = h("p", { class: "text-xs text-neutral-500 text-center" }, [
    "Spieler*innen können den Spielzug mit diesem Link oder dem QR-Code ansehen.",
  ]);

  host.appendChild(linkRow);
  host.appendChild(qrRow);
  host.appendChild(hint);
  host.appendChild(revokeBtn);
}

/**
 * Copy text with Clipboard-API + legacy execCommand fallback.
 * @param {string} text
 * @returns {Promise<void>}
 */
async function copyToClipboard(text) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback: hidden textarea + execCommand
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "absolute";
  ta.style.left = "-9999px";
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  if (!ok) {
    throw new Error("Clipboard copy failed");
  }
}
